/**
 * Graphes et chaînes de Markov — logique pure (Boîte à outils, Tle maths
 * expertes).
 *
 * Matrice d'adjacence : (Mᵏ)ᵢⱼ compte les chemins de longueur k de i à j ; on
 * les énumère aussi, pour les voir. Graphe probabiliste : probabilités en
 * dixièmes, πₙ = π₀Pⁿ exact, état stable π = πP exact par Gauss, et la
 * convergence vérifiée (une chaîne périodique tourne en rond).
 */

import {
  identity,
  matMul,
  matPow,
  q,
  solve,
  sub,
  ZERO,
  ONE,
  type Mat,
  type Q,
} from '../matrix-calc/matrix';

export const NAMES = ['A', 'B', 'C', 'D'] as const;

/* ------------------------------------------------------------------ */
/* matrice d'adjacence et chemins                                      */
/* ------------------------------------------------------------------ */

export type Adj = number[][];

export const adjMat = (adj: Adj): Mat => adj.map((r) => r.map((v) => q(v)));

/** puissance k de la matrice d'adjacence, en entiers */
export function adjPow(adj: Adj, k: number): number[][] {
  return matPow(adjMat(adj), k).map((r) => r.map((x) => Number(x.n)));
}

/**
 * Les chemins de longueur k de i à j (suites de k + 1 sommets), au plus
 * `limit` ; `total` est le nombre exact (celui de Mᵏ).
 */
export function paths(
  adj: Adj,
  i: number,
  j: number,
  k: number,
  limit = 24,
): { list: number[][]; total: number } {
  const list: number[][] = [];
  const walk = (cur: number[]) => {
    if (list.length >= limit) return;
    const last = cur[cur.length - 1]!;
    if (cur.length === k + 1) {
      if (last === j) list.push([...cur]);
      return;
    }
    adj[last]!.forEach((e, nxt) => {
      if (e) walk([...cur, nxt]);
    });
  };
  walk([i]);
  return { list, total: adjPow(adj, k)[i]![j]! };
}

export const pathText = (p: readonly number[]) => p.map((v) => NAMES[v]).join(' → ');

/* ------------------------------------------------------------------ */
/* graphe probabiliste                                                 */
/* ------------------------------------------------------------------ */

/** P en dixièmes (entiers, lignes de somme 10) → matrice exacte */
export const transition = (tenths: number[][]): Mat => tenths.map((r) => r.map((v) => q(v, 10)));

/** πₙ = π₀ Pⁿ (vecteur ligne) */
export function distribution(P: Mat, pi0: Q[], n: number): Q[] {
  return matMul([pi0], matPow(P, n))[0]!;
}

/** état stable : π = πP et somme 1 ; null s'il n'est pas unique */
export function stationary(P: Mat): Q[] | null {
  const n = P.length;
  // (Pᵀ − I) πᵀ = 0, la dernière équation remplacée par π₁ + … + πₙ = 1
  const A: Mat = Array.from({ length: n }, (_, j) =>
    Array.from({ length: n }, (_, l) => (j === n - 1 ? ONE : sub(P[l]![j]!, identity(n)[l]![j]!))),
  );
  const y = Array.from({ length: n }, (_, j) => (j === n - 1 ? ONE : ZERO));
  return solve(A, y);
}

/**
 * πₙ converge-t-il quel que soit π₀ ? Oui si et seulement si chaque classe
 * fermée (qu'on ne quitte plus) est apériodique : le pgcd des longueurs de
 * ses circuits vaut 1. Test exact sur le motif des probabilités non nulles
 * (les classes transitoires, qui se vident, ne comptent pas).
 */
export function converges(P: Mat): boolean {
  const n = P.length;
  const B = P.map((r) => r.map((x) => x.n !== 0n));
  // accessibilité (réflexive) par fermeture transitive
  const reach = B.map((r, i) => r.map((v, j) => v || i === j));
  for (let k = 0; k < n; k++)
    for (let i = 0; i < n; i++)
      for (let j = 0; j < n; j++) if (reach[i]![k] && reach[k]![j]) reach[i]![j] = true;
  const gcdN = (x: number, y: number): number => (y ? gcdN(y, x % y) : x);
  const boolMul = (X: boolean[][], Y: boolean[][]) =>
    X.map((r) => Y[0]!.map((_, j) => r.some((v, t) => v && Y[t]![j]!)));
  for (let i = 0; i < n; i++) {
    const cls = Array.from({ length: n }, (_, j) => j).filter((j) => reach[i]![j] && reach[j]![i]);
    if (cls[0] !== i) continue; // une classe, vue une fois
    const closed = cls.every((a) => B[a]!.every((v, b) => !v || cls.includes(b)));
    if (!closed) continue;
    // période : pgcd des k ≤ 2n² tels que (Bᵏ)ᵢᵢ soit vrai
    let period = 0;
    let Bk = B;
    for (let k = 1; k <= 2 * n * n; k++) {
      if (Bk[i]![i]) period = gcdN(period, k);
      Bk = boolMul(Bk, B);
    }
    if (period !== 1) return false;
  }
  return true;
}
